import { useEffect, useState } from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { useAppDispatch } from '../hooks/useRedux';
import { removeCart } from '../store/authSlice';
import { Mobile, Tablet, Desktop } from '../utils/MediaQuery';

interface CartProductProps {
	id: number;
	img: string;
	title: string;
	artist: string;
	price: number;
}

const Container = styled.div`
	width: 85%;
	margin: 0 auto;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: space-around;
	font-size: 1.2rem;

	& + & {
		margin-top: 4rem;
	}

	strong {
		font-weight: 600;
	}
`;

const ImgWrapper = styled.div`
	width: 10rem;
	padding: 2rem 0;

	img {
		width: 100%;
		height: 100%;
		border-radius: 8px;
	}
`;

const ProductInfo = styled.div``;

const Title = styled.div`
	font-weight: 500;
	margin-bottom: 1rem;
`;

const Artist = styled.div`
	font-size: 1rem;
`;

const CounterContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Counter = styled.div`
	margin: 0 0.4rem;
`;

const Price = styled.div`
	strong {
		font-weight: 600;
	}
`;

// Mobile Responsive
const MobileContainer = styled.div`
	width: 100%;
	margin: 0 auto;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: space-around;
	font-size: 1rem;

	& + & {
		margin-top: 2.4rem;
	}

	strong {
		font-weight: 600;
	}
`;

const MobileImgWrapper = styled.div`
	width: 5rem;
	padding: 1rem 0;

	img {
		width: 100%;
		height: 100%;
		border-radius: 8px;
	}
`;

const MobileInfoWrapper = styled.div`
	text-align: center;
`;

const MobileTitleAndArtist = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const MobileTitle = styled.div`
	font-weight: 500;
	margin-right: 0.4rem;
`;

const MobileArtist = styled.div`
	margin-left: 0.4rem;
`;

const MobileCounter = styled.div`
	margin: 0;
`;

// Tablet Responsive

const TabletContainer = styled(Container)`
	width: 100%;
`;

function CartProductCard({ id, img, title, artist, price }: CartProductProps): React.ReactElement {
	const dispatch = useAppDispatch();

	const [count, setCount] = useState(1);
	const [sumPrice, setSumPrice] = useState(price);

	const removeProduct = (id: number) => {
		dispatch(removeCart(id));
	};

	const handlePrice = (number: number) => {
		let price = number.toLocaleString();
		return price;
	};

	const handleCount = (action: string) => {
		if (action === 'minus' && count > 1) {
			setCount((prev) => prev - 1);
		} else if (action === 'plus') {
			setCount((prev) => prev + 1);
		}
	};

	useEffect(() => {
		setSumPrice(price * count);
	}, [count, price]);

	return (
		<>
			<Mobile>
				<MobileContainer>
					<MobileImgWrapper>
						<img src={`${img}`} alt={`${title} by ${artist}`}></img>
					</MobileImgWrapper>
					<MobileInfoWrapper>
						<MobileTitleAndArtist>
							<MobileTitle>{title}</MobileTitle>-<MobileArtist>{artist}</MobileArtist>
						</MobileTitleAndArtist>
						<CounterContainer>
							<IconButton size='large' onClick={() => handleCount('minus')}>
								<RemoveCircleOutlineIcon />
							</IconButton>
							<MobileCounter>
								<strong>{count}</strong> 개
							</MobileCounter>
							<IconButton size='large' onClick={() => handleCount('plus')}>
								<AddCircleOutlineIcon />
							</IconButton>
						</CounterContainer>
						<Price>
							<strong>{handlePrice(sumPrice)}</strong> 원
						</Price>
					</MobileInfoWrapper>
					<IconButton size='large' onClick={() => removeProduct(id)}>
						<ClearIcon />
					</IconButton>
				</MobileContainer>
			</Mobile>

			<Tablet>
				<TabletContainer>
					<ImgWrapper>
						<img src={`${img}`} alt={`${title} by ${artist}`}></img>
					</ImgWrapper>
					<ProductInfo>
						<Title>{title}</Title>
						<Artist>{artist}</Artist>
					</ProductInfo>
					<CounterContainer>
						<IconButton size='large' onClick={() => handleCount('minus')}>
							<RemoveCircleOutlineIcon />
						</IconButton>
						<Counter>
							<strong>{count}</strong> 개
						</Counter>
						<IconButton size='large' onClick={() => handleCount('plus')}>
							<AddCircleOutlineIcon />
						</IconButton>
					</CounterContainer>
					<Price>
						<strong>{handlePrice(sumPrice)}</strong> 원
					</Price>
					<IconButton size='large' onClick={() => removeProduct(id)}>
						<ClearIcon />
					</IconButton>
				</TabletContainer>
			</Tablet>

			<Desktop>
				<Container>
					<ImgWrapper>
						<img src={`${img}`} alt={`${title} by ${artist}`}></img>
					</ImgWrapper>
					<ProductInfo>
						<Title>{title}</Title>
						<Artist>{artist}</Artist>
					</ProductInfo>
					<CounterContainer>
						<IconButton size='large' onClick={() => handleCount('minus')}>
							<RemoveCircleOutlineIcon />
						</IconButton>
						<Counter>
							<strong>{count}</strong> 개
						</Counter>
						<IconButton size='large' onClick={() => handleCount('plus')}>
							<AddCircleOutlineIcon />
						</IconButton>
					</CounterContainer>
					<Price>
						<strong>{handlePrice(sumPrice)}</strong> 원
					</Price>
					<IconButton size='large' onClick={() => removeProduct(id)}>
						<ClearIcon />
					</IconButton>
				</Container>
			</Desktop>
		</>
	);
}

export default CartProductCard;
